<template>
  <div
    :class="cardClass"
    role="group"
    :aria-label="cardCaption"
    :data-label="cardCaption"
    data-label-type="group-title"
  >
    <div v-if="$slots.marker" class="cat-card-marker">
      <slot name="marker"></slot>
    </div>

    <div class="cat-card-content">
      <div v-if="$slots.header" class="cat-card-header">
        <div class="cat-card-header-content">
          <slot name="header"></slot>
        </div>
        <div v-if="!actionsAlignContent" class="cat-card-actions">
          <slot name="actions"></slot>
        </div>
      </div>

      <div class="cat-card-body">
        <slot></slot>
      </div>

      <div v-if="$slots.footer" class="cat-card-footer">
        <slot name="footer"></slot>
      </div>
    </div>

    <div v-if="actionsAlignContent" class="cat-card-actions">
      <slot name="actions"></slot>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, toNative } from "vue-facing-decorator";
import { PropOptions } from "vue";
import Helpful from "../utilities/helpful.ts";
import PageElementMixin from "../field-element.ts";
import CatIcon from "@/components/utilities/catIcon.vue";

@Component({
  name: "cat-card",
  components: { CatIcon },
  mixins: [Helpful, PageElementMixin]
})
class CatCard extends Vue {
  @Prop({
    default: null,
    type: String,
    description:
      "Any text that can be used to identify this card in the accessibility role group and data-label attributes."
  } as PropOptions<any>)
  readonly cardCaption?: string;
  @Prop({
    type: Boolean,
    description: "Border around the card container."
  } as PropOptions)
  readonly outlined?: boolean;
  @Prop({
    type: Boolean,
    description: "Box shadow around the card container."
  } as PropOptions)
  readonly elevated?: boolean;
  @Prop({
    type: Boolean,
    description: "Background color for the card container."
  } as PropOptions)
  readonly opaque?: boolean;
  @Prop({
    type: Boolean,
    description: "Header with horizontal line."
  } as PropOptions)
  readonly splitHeader?: boolean;
  @Prop({
    type: Boolean,
    description: "Aligns the 'actions' slot to the top of the card container."
  } as PropOptions)
  readonly actionAlignsTop?: boolean;
  @Prop({
    type: Boolean,
    description:
      "Aligns the 'actions' slot to the middle of the card container."
  } as PropOptions)
  readonly actionAlignsMiddle?: boolean;
  @Prop({
    type: Boolean,
    description: "Aligns the 'marker' slot to the right of the card container."
  } as PropOptions)
  readonly markerRight?: boolean;

  get cardClass(): object {
    return {
      "cat-card": true,
      "card-actions-middle": this.actionAlignsMiddle,
      outlined: this.outlined,
      elevated: this.elevated,
      opaque: this.opaque,
      splitHeader: this.splitHeader,
      marker: this.$slots.marker && !this.markerRight,
      "marker-right": this.$slots.marker && this.markerRight
    };
  }

  /**
   * Move the actions DOM outside the header when
   * slots:header is undefined and slots:action is true
   * either actionAlignsTop or actionAlignsMiddle is true
   */
  get actionsAlignContent(): boolean {
    const moveActions =
      this.actionAlignsTop || this.actionAlignsMiddle ? true : false;
    return !!(moveActions || (!this.$slots.header && this.$slots.actions));
  }
}

export default toNative(CatCard);
</script>
